type Rgb = [number, number, number]

// WCAG 2.1, nível AA para texto normal.
export const MIN_CONTRAST = 4.5

// Defaults legíveis: branco e neutral.800 do tema (o mesmo do H1 da categoria).
export const READABLE_LIGHT = '#FFFFFF'
export const READABLE_DARK = '#363843'

const NAMED_COLORS: Record<string, Rgb> = {
  white: [255, 255, 255],
  black: [0, 0, 0],
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
const RGB =
  /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/i

export const parseColor = (color?: string | null): Rgb | null => {
  const value = color?.trim().toLowerCase()
  if (!value) return null

  if (NAMED_COLORS[value]) return NAMED_COLORS[value]

  const hex = value.match(HEX)?.[1]
  if (hex) {
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((digit) => digit + digit)
            .join('')
        : hex

    return [0, 2, 4].map((start) =>
      parseInt(full.slice(start, start + 2), 16)
    ) as Rgb
  }

  const rgb = value.match(RGB)
  if (rgb) {
    const channels = rgb.slice(1, 4).map(Number)
    return channels.every((channel) => channel <= 255)
      ? (channels as Rgb)
      : null
  }

  return null
}

const relativeLuminance = ([r, g, b]: Rgb) => {
  const [red, green, blue] = [r, g, b].map((channel) => {
    const value = channel / 255
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  })

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

// Sem arredondar: no limiar o WCAG não admite arredondamento (4,49995 não é 4,5).
const rawContrast = (foreground: Rgb, background: Rgb) => {
  const [lighter, darker] = [
    relativeLuminance(foreground),
    relativeLuminance(background),
  ].sort((a, b) => b - a)

  return (lighter + 0.05) / (darker + 0.05)
}

export const contrastRatio = (
  foreground?: string | null,
  background?: string | null
): number | null => {
  const front = parseColor(foreground)
  const back = parseColor(background)
  if (!front || !back) return null

  return Math.round(rawContrast(front, back) * 100) / 100
}

// Pior caso entre os fundos: num degradê, o texto precisa ser legível nas duas pontas.
const worstContrast = (color: string, backgrounds: string[]) =>
  Math.min(
    ...backgrounds.map((background) =>
      rawContrast(parseColor(color)!, parseColor(background)!)
    )
  )

/**
 * Mantém a cor configurada se ela tiver contraste mínimo contra TODOS os fundos
 * (as duas cores do degradê); senão, o default (branco ou neutral.800) com mais
 * contraste no pior caso. Fundo ilegível é ignorado.
 */
export const pickReadableColor = (
  preferred: string | null | undefined,
  backgrounds: Array<string | null | undefined>
): string => {
  const readableBackgrounds = backgrounds.filter(
    (background): background is string => !!parseColor(background)
  )

  if (preferred && parseColor(preferred)) {
    if (!readableBackgrounds.length) return preferred
    if (worstContrast(preferred, readableBackgrounds) >= MIN_CONTRAST)
      return preferred
  }

  if (!readableBackgrounds.length) return READABLE_DARK

  return worstContrast(READABLE_LIGHT, readableBackgrounds) >=
    worstContrast(READABLE_DARK, readableBackgrounds)
    ? READABLE_LIGHT
    : READABLE_DARK
}
